/* TFS Version: 1.0 */

if (!window.TFSAudioPlayer) {
  class TFSPlayer {
    /**
     * Creates a new TFSAudioPlayer instance.
     * @param {Object} config - Configuration object for the player.
     */
    constructor(config) {
      this.playerEl = document.getElementById(config.playerId);
      if (!this.playerEl) {
        throw new Error(`Element with id "${config.playerId}" not found`);
      }

      // Initialize playlist and track management
      this.playlist = config.playlist || [];
      this.currentTrackIndex = 0;
      this.sound = null;
      this.isPlaying = false;

      // Volume management
      this.volumeScale = 100;

      // UI configuration
      this.showSpeedButton = config.showSpeedButton || false;
      this.showShuffleButton = config.showShuffleButton || false;
      this.showPlaylistButton = config.showPlaylistButton || false;
      this.showPlaylistByDefault = config.showPlaylistByDefault || false;

      // Playback speed management
      this.speeds = [0.5, 1, 1.5, 2];
      this.currentSpeedIndex = 1;
      this.speed = 1;

      // Shuffle mode
      this.shuffleMode = false;
      this.shuffledPlaylist = [];
      this.shuffleIndex = 0;

      // Select and initialize UI controls
      this.initializeControls();

      // Progress bar hover functionality
      this.progressBar = this.playerEl.querySelector("[data-progress-bar]");
      this.currentTimeDisplay = this.playerEl.querySelector("[data-current-time]");
      this.isHovering = false;
      this.hoverTime = 0;

      // Set up event listeners and initialize player state
      this.createAdditionalControls();
      this.addEventListeners();
      this.updatePlaylist();
      if (this.playlist.length > 0) {
        this.loadTrack(0);
      }

      this.togglePlaylistVisibility(this.showPlaylistByDefault);

      // Set up progress update interval
      setInterval(() => this.updateProgress(), 100);

      // Add this instance to the static array of all TFSAudioPlayer instances
      TFSPlayer.instances.push(this);

      // Set up progress bar hover functionality
      this.setupProgressBarHover();

      // Add mute state
      this.isMuted = false;
    }

    /**
     * Initializes the UI controls.
     */
    initializeControls() {
      this.controls = {
        playPause: this.playerEl.querySelector(".tfs-player-play-pause"),
        stop: this.playerEl.querySelector(".tfs-player-stop"),
        prev: this.playerEl.querySelector(".tfs-player-prev"),
        next: this.playerEl.querySelector(".tfs-player-next"),
        progressBar: this.playerEl.querySelector(".tfs-player-progress-bar"),
        progress: this.playerEl.querySelector(".tfs-player-progress"),
        currentTime: this.playerEl.querySelector(".tfs-player-current-time"),
        totalTime: this.playerEl.querySelector(".tfs-player-total-time"),
        volumeControl: this.playerEl.querySelector(".tfs-player-volume-control"),
        currentTrack: this.playerEl.querySelector(".tfs-player-current-track"),
        playlist: this.playerEl.querySelector(".tfs-player-playlist"),
        speed: this.playerEl.querySelector(".tfs-player-speed"),
        shuffle: this.playerEl.querySelector(".tfs-player-shuffle"),
        playlistToggle: this.playerEl.querySelector(".tfs-player-playlist-toggle"),
        volumeIcon: this.playerEl.querySelector(".tfs-player-volume-icon"),
      };
    }

    /**
     * Creates additional control buttons based on configuration.
     */
    createAdditionalControls() {
      const controlsContainer = this.playerEl.querySelector(".tfs-player-controls");

      if (this.showSpeedButton && !this.controls.speed) {
        this.controls.speed = document.createElement("button");
        this.controls.speed.className = "tfs-player-speed";
        this.controls.speed.innerHTML = "1.0x";
        controlsContainer.appendChild(this.controls.speed);
      }

      if (this.showShuffleButton && !this.controls.shuffle) {
        const shuffleButtonSVG =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none"><path d="M19.5576 4L20.4551 4.97574C20.8561 5.41165 21.0566 5.62961 20.9861 5.81481C20.9155 6 20.632 6 20.0649 6C18.7956 6 17.2771 5.79493 16.1111 6.4733C15.3903 6.89272 14.8883 7.62517 14.0392 9M3 18H4.58082C6.50873 18 7.47269 18 8.2862 17.5267C9.00708 17.1073 9.50904 16.3748 10.3582 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M19.5576 20L20.4551 19.0243C20.8561 18.5883 21.0566 18.3704 20.9861 18.1852C20.9155 18 20.632 18 20.0649 18C18.7956 18 17.2771 18.2051 16.1111 17.5267C15.2976 17.0534 14.7629 16.1815 13.6935 14.4376L10.7038 9.5624C9.63441 7.81853 9.0997 6.9466 8.2862 6.4733C7.47269 6 6.50873 6 4.58082 6H3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>';
        this.controls.shuffle = document.createElement("button");
        this.controls.shuffle.className = "tfs-player-shuffle";
        this.controls.shuffle.innerHTML = shuffleButtonSVG;
        controlsContainer.appendChild(this.controls.shuffle);
      }

      if (this.showPlaylistButton && !this.controls.playlistToggle) {
        const playlistButtonSVG =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none"><path d="M2 3L20 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M2 10L15 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M2 17L9 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M18.25 19C18.25 20.6569 16.8509 22 15.125 22C13.3991 22 12 20.6569 12 19C12 17.3431 13.3991 16 15.125 16C16.8509 16 18.25 17.3431 18.25 19ZM18.25 19V10C18.6667 10.6 19 13.12 22 13.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>';
        this.controls.playlistToggle = document.createElement("button");
        this.controls.playlistToggle.className = "tfs-player-playlist-toggle";
        this.controls.playlistToggle.innerHTML = playlistButtonSVG;
        controlsContainer.appendChild(this.controls.playlistToggle);
      }
    }

    /**
     * Adds event listeners to player controls.
     */
    addEventListeners() {
      if (this.controls.playPause) {
        this.controls.playPause.addEventListener("click", () => this.togglePlayPause());
      }
      if (this.controls.stop) {
        this.controls.stop.addEventListener("click", () => this.stopTrack());
      }
      if (this.controls.prev) {
        this.controls.prev.addEventListener("click", () => this.playPrev());
      }
      if (this.controls.next) {
        this.controls.next.addEventListener("click", () => this.playNext());
      }
      if (this.controls.progressBar) {
        this.controls.progressBar.addEventListener("click", (e) => this.seek(e));
      }
      if (this.controls.volumeControl) {
        this.controls.volumeControl.addEventListener("input", () => this.updateVolume());
      }
      if (this.controls.speed) {
        this.controls.speed.addEventListener("click", () => this.toggleSpeed());
      }
      if (this.controls.shuffle) {
        this.controls.shuffle.addEventListener("click", () => this.toggleShuffle());
      }
      if (this.controls.playlistToggle) {
        this.controls.playlistToggle.addEventListener("click", () => this.togglePlaylist());
      }

      const volumeSlider = this.playerEl.querySelector(".tfs-player-volume-control");
      volumeSlider.addEventListener("input", () => this.updateVolume());

      // Initial update of volume slider appearance
      this.updateVolume();

      if (this.controls.volumeIcon) {
        this.controls.volumeIcon.addEventListener("click", () => this.toggleMute());
      }
    }

    /**
     * Updates the playlist UI.
     */
    updatePlaylist() {
      if (!this.controls.playlist) return;

      this.controls.playlist.innerHTML = "";
      this.playlist.forEach((track, index) => {
        const li = document.createElement("li");
        li.textContent = track.title;
        if (index === this.currentTrackIndex) {
          li.classList.add("active");
        }
        li.addEventListener("click", () => {
          this.loadTrack(index);
          this.playTrack();
        });
        this.controls.playlist.appendChild(li);
      });
    }

    /**
     * Loads a track at the specified index.
     * @param {number} index - Index of the track to load.
     */
    loadTrack(index) {
      if (this.sound) {
        this.sound.unload();
      }
      this.currentTrackIndex = index;

      let volume = 1;
      if (this.controls.volumeControl) {
        volume = parseInt(this.controls.volumeControl.value) / this.volumeScale;
      }

      this.sound = new Howl({
        src: [this.playlist[index].file],
        html5: true,
        volume: volume,
        onend: () => this.playNext(),
        onload: () => this.updateTotalTime(),
      });

      this.updateCurrentTrack();
      this.updatePlaylist();
    }

    /**
     * Plays the current track and stops all other instances.
     */
    playTrack() {
      // Stop all other instances before playing
      TFSPlayer.stopAllExcept(this);

      if (this.sound) {
        this.sound.play();
        this.isPlaying = true;
        this.sound.rate(this.speed);
        this.updatePlayPauseButton();
      }
    }

    /**
     * Stops the current track.
     */
    stopTrack() {
      if (this.sound) {
        this.sound.stop();
        this.isPlaying = false;
        this.updatePlayPauseButton();
      }
    }

    /**
     * Toggles play/pause state of the current track.
     *
     * This method handles the play/pause functionality. When called:
     * 1. If the track is currently playing, it pauses the track.
     * 2. If the track is paused, it stops all other playing instances first,
     *    then plays the current track.
     * 3. It updates the play/pause button appearance to reflect the new state.
     *
     * This ensures that only one track plays at a time across all player instances.
     */
    togglePlayPause() {
      if (this.sound) {
        if (this.isPlaying) {
          this.sound.pause();
          this.isPlaying = false;
        } else {
          // Stop all other instances before playing
          TFSPlayer.stopAllExcept(this);
          this.sound.play();
          this.isPlaying = true;
        }
        this.updatePlayPauseButton();
      }
    }

    /**
     * Plays the next track in the playlist or the next shuffled track if shuffle is on.
     *
     * This method determines the next track to play based on the current mode:
     * 1. In shuffle mode, it moves to the next index in the shuffled playlist.
     * 2. In normal mode, it moves to the next track in the original playlist order.
     *
     * After determining the next track, it loads and plays it.
     */
    playNext() {
      if (this.shuffleMode) {
        this.shuffleIndex = (this.shuffleIndex + 1) % this.playlist.length;
        this.currentTrackIndex = this.shuffledPlaylist[this.shuffleIndex];
      } else {
        this.currentTrackIndex = (this.currentTrackIndex + 1) % this.playlist.length;
      }
      this.loadTrack(this.currentTrackIndex);
      this.playTrack();
    }

    /**
     * Plays the previous track in the playlist or the previous shuffled track if shuffle is on.
     *
     * This method determines the previous track to play based on the current mode:
     * 1. In shuffle mode, it moves to the previous index in the shuffled playlist.
     * 2. In normal mode, it moves to the previous track in the original playlist order.
     *
     * After determining the previous track, it loads and plays it.
     */
    playPrev() {
      if (this.shuffleMode) {
        this.shuffleIndex = (this.shuffleIndex - 1 + this.playlist.length) % this.playlist.length;
        this.currentTrackIndex = this.shuffledPlaylist[this.shuffleIndex];
      } else {
        this.currentTrackIndex =
          (this.currentTrackIndex - 1 + this.playlist.length) % this.playlist.length;
      }
      this.loadTrack(this.currentTrackIndex);
      this.playTrack();
    }

    /**
     * Seeks to a specific position in the current track.
     * @param {Event} e - Click event on the progress bar.
     */
    seek(e) {
      if (this.sound && this.controls.progressBar) {
        const percent = e.offsetX / this.controls.progressBar.offsetWidth;
        const seekTime = percent * this.sound.duration();
        this.sound.seek(seekTime);
        this.updateProgress();
      }
    }

    /**
     * Updates the volume of the current track and the appearance of the volume slider.
     */
    updateVolume() {
      if (this.controls.volumeControl) {
        const volume = parseFloat(this.controls.volumeControl.value);
        if (this.sound) {
          this.sound.volume(volume / 100);
        }

        const percentage = volume;
        this.controls.volumeControl.style.background = `linear-gradient(to right, var(--bar-active) 0%, var(--bar-active) ${percentage}%, var(--bar-background) ${percentage}%, var(--bar-background) 100%)`;
      }

      this.updateVolumeIcon();
    }

    /**
     * Toggles mute state and updates the volume icon.
     */
    toggleMute() {
      this.isMuted = !this.isMuted;
      if (this.isMuted) {
        this.previousVolume = this.controls.volumeControl.value;
        this.controls.volumeControl.value = 0;
      } else {
        this.controls.volumeControl.value = this.previousVolume || 50;
      }
      this.updateVolume();
      this.updateVolumeIcon();
    }

    /**
     * Updates the volume icon based on the mute state.
     */
    updateVolumeIcon() {
      if (!this.controls.volumeIcon) return;

      const volumeOnSVG =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none"><path d="M14 14.8135V9.18646C14 6.04126 14 4.46866 13.0747 4.0773C12.1494 3.68593 11.0603 4.79793 8.88232 7.02192C7.75439 8.17365 7.11085 8.42869 5.50604 8.42869C4.10257 8.42869 3.40084 8.42869 2.89675 8.77262C1.85035 9.48655 2.00852 10.882 2.00852 12C2.00852 13.118 1.85035 14.5134 2.89675 15.2274C3.40084 15.5713 4.10257 15.5713 5.50604 15.5713C7.11085 15.5713 7.75439 15.8264 8.88232 16.9781C11.0603 19.2021 12.1494 20.3141 13.0747 19.9227C14 19.5313 14 17.9587 14 14.8135Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M17 9.5C17.8 10.3 18.5 11.5 18.5 12C18.5 12.5 17.8 13.7 17 14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" /></svg>';

      const volumeOffSVG =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none"><path d="M14 14.8135V9.18646C14 6.04126 14 4.46866 13.0747 4.0773C12.1494 3.68593 11.0603 4.79793 8.88232 7.02192C7.75439 8.17365 7.11085 8.42869 5.50604 8.42869C4.10257 8.42869 3.40084 8.42869 2.89675 8.77262C1.85035 9.48655 2.00852 10.882 2.00852 12C2.00852 13.118 1.85035 14.5134 2.89675 15.2274C3.40084 15.5713 4.10257 15.5713 5.50604 15.5713C7.11085 15.5713 7.75439 15.8264 8.88232 16.9781C11.0603 19.2021 12.1494 20.3141 13.0747 19.9227C14 19.5313 14 17.9587 14 14.8135Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M18 10L22 14M18 14L22 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" /></svg>';

      this.controls.volumeIcon.innerHTML = this.isMuted ? volumeOffSVG : volumeOnSVG;
    }

    /**
     * Updates the progress bar and current time display.
     */
    updateProgress() {
      if (this.sound && this.controls.progress) {
        const seek = this.sound.seek() || 0;
        const percent = (seek / this.sound.duration()) * 100 || 0;
        this.controls.progress.style.width = `${percent}%`;
        if (!this.isHovering) {
          this.updateTimeInfo();
        }
      }
    }

    /**
     * Updates the current time display.
     * @param {number} time - Current time in seconds.
     */
    updateCurrentTime(time) {
      if (this.controls.currentTime) {
        this.controls.currentTime.textContent = this.formatTime(time);
      }
    }

    /**
     * Updates the total time display for the current track.
     */
    updateTotalTime() {
      if (this.sound && this.controls.totalTime) {
        const duration = this.sound.duration();
        this.controls.totalTime.textContent = this.formatTime(duration);
        this.updateCurrentTrack();
      }
    }

    /**
     * Updates the display of the current track title.
     */
    updateCurrentTrack() {
      if (this.controls.currentTrack) {
        this.controls.currentTrack.textContent = this.playlist[this.currentTrackIndex].title;
      }
    }

    /**
     * Updates the play/pause button icon based on the current playing state.
     */
    updatePlayPauseButton() {
      const pauseSVG =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none"><path d="M4 7C4 5.58579 4 4.87868 4.43934 4.43934C4.87868 4 5.58579 4 7 4C8.41421 4 9.12132 4 9.56066 4.43934C10 4.87868 10 5.58579 10 7V17C10 18.4142 10 19.1213 9.56066 19.5607C9.12132 20 8.41421 20 7 20C5.58579 20 4.87868 20 4.43934 19.5607C4 19.1213 4 18.4142 4 17V7Z" stroke="currentColor" stroke-width="1.5" /><path d="M14 7C14 5.58579 14 4.87868 14.4393 4.43934C14.8787 4 15.5858 4 17 4C18.4142 4 19.1213 4 19.5607 4.43934C20 4.87868 20 5.58579 20 7V17C20 18.4142 20 19.1213 19.5607 19.5607C19.1213 20 18.4142 20 17 20C15.5858 20 14.8787 20 14.4393 19.5607C14 19.1213 14 18.4142 14 17V7Z" stroke="currentColor" stroke-width="1.5" /></svg>';

      const playSVG =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none"><path d="M18.8906 12.846C18.5371 14.189 16.8667 15.138 13.5257 17.0361C10.296 18.8709 8.6812 19.7884 7.37983 19.4196C6.8418 19.2671 6.35159 18.9776 5.95624 18.5787C5 17.6139 5 15.7426 5 12C5 8.2574 5 6.3861 5.95624 5.42132C6.35159 5.02245 6.8418 4.73288 7.37983 4.58042C8.6812 4.21165 10.296 5.12907 13.5257 6.96393C16.8667 8.86197 18.5371 9.811 18.8906 11.154C19.0365 11.7084 19.0365 12.2916 18.8906 12.846Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>';

      const buttonIcon = this.isPlaying ? pauseSVG : playSVG;

      if (this.controls.playPause) {
        this.controls.playPause.innerHTML = buttonIcon;
      }
    }

    /**
     * Formats time in seconds to MM:SS format.
     * @param {number} seconds - Time in seconds.
     * @returns {string} Formatted time string.
     */
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    }

    /**
     * Toggles playback speed.
     */
    toggleSpeed() {
      this.currentSpeedIndex = (this.currentSpeedIndex + 1) % this.speeds.length;
      this.speed = this.speeds[this.currentSpeedIndex];
      if (this.sound) {
        this.sound.rate(this.speed);
      }
      if (this.controls.speed) {
        this.controls.speed.textContent = `${this.speed}x`;
      }
    }

    /**
     * Toggles shuffle mode and generates a new shuffled playlist if enabled.
     */
    toggleShuffle() {
      this.shuffleMode = !this.shuffleMode;
      if (this.shuffleMode) {
        this.generateShuffledPlaylist();
      }
      if (this.controls.shuffle) {
        this.controls.shuffle.classList.toggle("active", this.shuffleMode);
      }
    }

    /**
     * Generates a new shuffled playlist.
     *
     * This method creates a randomized version of the playlist:
     * 1. It starts with an array of indices representing the original playlist order.
     * 2. It then shuffles these indices using the Fisher-Yates shuffle algorithm.
     * 3. Finally, it finds the current track's position in the shuffled list to maintain continuity.
     *
     * This approach ensures a random but complete playthrough of all tracks before any repeats.
     */
    generateShuffledPlaylist() {
      this.shuffledPlaylist = [...Array(this.playlist.length).keys()];
      for (let i = this.shuffledPlaylist.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.shuffledPlaylist[i], this.shuffledPlaylist[j]] = [
          this.shuffledPlaylist[j],
          this.shuffledPlaylist[i],
        ];
      }
      this.shuffleIndex = this.shuffledPlaylist.indexOf(this.currentTrackIndex);
    }

    /**
     * Toggles playlist visibility.
     */
    togglePlaylist() {
      this.togglePlaylistVisibility(
        !this.controls.playlist.style.display || this.controls.playlist.style.display === "none",
      );
    }

    /**
     * Sets playlist visibility.
     * @param {boolean} show - Whether to show or hide the playlist.
     */
    togglePlaylistVisibility(show) {
      if (this.controls.playlist) {
        this.controls.playlist.style.display = show ? "block" : "none";
      }
    }

    /**
     * Sets up event listeners for progress bar hover functionality.
     */
    setupProgressBarHover() {
      this.progressBar.addEventListener("mousemove", this.handleProgressBarHover.bind(this));
      this.progressBar.addEventListener("mouseenter", () => {
        this.isHovering = true;
        this.updateTimeInfo();
      });
      this.progressBar.addEventListener("mouseleave", this.handleProgressBarMouseOut.bind(this));
    }

    /**
     * Handles mouse movement over the progress bar.
     * @param {MouseEvent} event - The mouse event.
     */
    handleProgressBarHover(event) {
      const rect = this.progressBar.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const width = rect.width;
      const duration = this.sound.duration();
      this.hoverTime = (x / width) * duration;

      this.updateTimeInfo();
    }

    /**
     * Handles mouse leaving the progress bar.
     */
    handleProgressBarMouseOut() {
      this.isHovering = false;
      this.updateTimeInfo();
    }

    /**
     * Updates the time information display.
     */
    updateTimeInfo() {
      if (this.isHovering) {
        this.currentTimeDisplay.textContent = this.formatTime(this.hoverTime);
      } else {
        const currentTime = this.sound.seek() || 0;
        this.currentTimeDisplay.textContent = this.formatTime(currentTime);
      }
    }

    /**
     * Initializes a new TFSAudioPlayer instance.
     * @param {Object} config - Configuration object for the player.
     * @returns {TFSPlayer} New TFSAudioPlayer instance.
     */
    static init(config) {
      return new TFSPlayer(config);
    }

    // Static property to store all TFSAudioPlayer instances
    static instances = [];

    /**
     * Stops playback on all TFSAudioPlayer instances except the specified one.
     * @param {TFSPlayer} currentInstance - The instance to keep playing.
     *
     * This static method is used to ensure only one track plays at a time across all player instances:
     * 1. It iterates through all TFSAudioPlayer instances.
     * 2. For each instance that is not the current one, it stops playback.
     *
     * This is called before playing a track to prevent multiple tracks from playing simultaneously.
     */
    static stopAllExcept(currentInstance) {
      TFSPlayer.instances.forEach((instance) => {
        if (instance !== currentInstance) {
          instance.stopTrack();
        }
      });
    }
  }

  window.TFSAudioPlayer = TFSPlayer;
}

if (!customElements.get("tfs-audio-player")) {
  customElements.define(
    "tfs-audio-player",
    class TFSAudioPlayer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const playerId = this.getAttribute("data-player-id");
        const playlistData = this.getAttribute("data-playlist");
        let playlist = [];

        try {
          playlist = JSON.parse(playlistData);
        } catch (e) {
          console.error("Invalid playlist data", e);
        }

        const config = {
          playerId: playerId,
          playlist: playlist,
          showPlaylistButton: this.getAttribute("data-playlist-button") === "true",
          showPlaylistByDefault: this.getAttribute("data-playlist-on") === "true",
          showSpeedButton: this.getAttribute("data-playlist-speed-control") === "true",
          showShuffleButton: this.getAttribute("data-playlist-shuffle") === "true",
        };

        this.playerInstance = window.TFSAudioPlayer.init(config);
      }

      disconnectedCallback() {
        if (this.playerInstance && this.playerInstance.sound) {
          this.playerInstance.sound.unload();
          this.playerInstance = null;
        }
      }
    },
  );
}
